// Provee el contenido de la landing vía React Context.
//
// Estrategia (A3): la página arranca con los constantes (render instantáneo, sin
// pantalla en blanco) y, al montar, hace fetch de content.json —un JSON que el
// sistema incap-system publica en el mismo hosting— y fusiona lo que venga sobre
// los defaults. Si el fetch falla (404 / red / JSON inválido) se queda con los
// constantes. Así, editar contenido en el sistema NO requiere recompilar la landing.
//
// MODO PREVIEW: cuando la landing se abre DENTRO del panel del sistema (en un iframe,
// con ?preview en la URL), en vez del content.json publicado lee el contenido EN VIVO
// del backend (parámetro ?src=<endpoint de preview>) y escucha postMessage para
// reflejar los cambios del editor al instante, sin guardar ni publicar. Fuera de ese
// modo, el comportamiento es idéntico al de siempre.

import React, { createContext, useContext, useEffect, useState } from "react";
import { defaultContent, CONTENT_KEYS } from "./defaults.js";

const SiteContentContext = createContext(defaultContent);

// URL del JSON publicado. Por defecto, mismo origen (/content.json). Se puede
// apuntar a otra URL con REACT_APP_CONTENT_URL en tiempo de build.
const CONTENT_URL = process.env.REACT_APP_CONTENT_URL || "/content.json";

// Lee la config de preview de la URL (?preview y opcional ?src=<endpoint en vivo>).
// Devuelve null si no estamos en modo preview.
function readPreviewConfig() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("preview")) return null;
    return { src: params.get("src") || CONTENT_URL };
  } catch (e) {
    return null;
  }
}

// Fusión superficial por clave conocida: aplica el valor del JSON solo si viene
// definido (!= null); si falta, conserva el default. Un JSON totalmente inválido
// (error de parseo) se ignora por completo (catch abajo) y se usan los defaults.
function mergeContent(fetched) {
  if (!fetched || typeof fetched !== "object") return defaultContent;
  const merged = { ...defaultContent };
  for (const key of CONTENT_KEYS) {
    if (fetched[key] != null) merged[key] = fetched[key];
  }
  return merged;
}

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    let cancelled = false;
    const preview = readPreviewConfig();
    const url = preview ? preview.src : CONTENT_URL;

    // cache: "no-cache" fuerza revalidación con el servidor, para que un contenido
    // recién publicado (o guardado, en preview) aparezca sin quedar cacheado.
    const load = () => {
      fetch(url, { cache: "no-cache" })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (!cancelled) setContent(mergeContent(data));
        })
        .catch(() => {
          // Silencioso: se conserva el contenido por defecto (constantes).
        });
    };
    load();

    // Solo en preview: recibe del panel el contenido en vivo (para reflejar cambios sin
    // guardar) o una orden de recargar (tras guardar). No hace nada fuera de ese modo.
    let onMessage;
    if (preview) {
      onMessage = (event) => {
        const data = event && event.data;
        if (!data || typeof data !== "object") return;
        if (data.type === "incap-preview-content" && data.content) {
          setContent(mergeContent(data.content));
        } else if (data.type === "incap-preview-refetch") {
          load();
        }
      };
      window.addEventListener("message", onMessage);
      // Avisa al panel (contenedor) que el preview ya está listo para recibir mensajes.
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: "incap-preview-ready" }, "*");
        }
      } catch (e) {
        /* noop */
      }
    }

    return () => {
      cancelled = true;
      if (onMessage) window.removeEventListener("message", onMessage);
    };
  }, []);

  return (
    <SiteContentContext.Provider value={content}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
